import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  Button,
  CartIcon,
  HelpTip,
  Horizontal,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../Experience/ExperienceProvider';
import {useTranslation} from 'react-i18next';
import {hasValidPageCount} from '../../Utils/Page';
import {DialogStatus} from '../../Enums';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {LayoutSelector, PagesSelector} from '../../Selectors';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import UploadWarningTip from '../Tips/UploadWarningTip';
import {Lang, Localisation, ViewMode} from '@taopix/taopix-core';
import {PageInterface} from '@taopix/taopix-core/dist/types/types';
import {useAssetStats} from '../Context/Assets/AssetStatsContext';

type OrderButtonProps = {} & React.HTMLAttributes<HTMLButtonElement>;

type OrderButtonTipState = {
  readyToOrder: boolean;
  uploadProgress: boolean;
  minimunNumOfPrints: boolean;
  textIsProcessing: boolean;
};

const OrderButton = ({...props}: OrderButtonProps) => {
  const userFlow = useUserFlowState();
  const ui = useUiState();
  const {experience} = useExperience();
  const {
    state: {hiresPercentage, totalPercentage, total},
  } = useAssetStats();
  const productLayout = useSelector(LayoutSelector);
  const layout = useSelector(LayoutSelector);
  const pages = useSelector(PagesSelector);
  const {t} = useTranslation(['*', 'helptips']);
  const orderButtonRef = useRef<HTMLButtonElement>(null);

  const [tipState, setTipState] = useState<OrderButtonTipState>({
    readyToOrder: false,
    uploadProgress: false,
    minimunNumOfPrints: false,
    textIsProcessing: false,
  });

  const [showProgress, setShowProgress] = useState(false);

  const min = layout.minimumprintsperproject;
  const max = layout.maximumprintsperproject;

  // calculate the total number of prints across all pages
  const totalPrints = useMemo(() => {
    return pages.reduce((acc: number, curr: PageInterface) => {
      if (0 === curr.qty) {
        acc += 1;
      } else {
        acc += curr.qty;
      }
      return acc;
    }, 0);
  }, [pages]);

  useEffect(() => {
    if (hiresPercentage >= 100) {
      setTipState(current => {
        return {
          ...current,
          readyToOrder: showProgress,
          uploadProgress: false,
        };
      });
      setShowProgress(false);
    }
  }, [hiresPercentage]);

  useEffect(() => {
    if (
      !ui.state.flags.coverNeedsChecking &&
      DialogStatus.Seen === ui.state.dialogs.coverSpineChangeNotification
    ) {
      const delay = ui.state.viewMode === ViewMode.Grid ? 0 : 2000;
      setTimeout(() => {
        setTipState(current => {
          return {
            ...current,
            readyToOrder: false,
            uploadProgress: false,
          };
        });
      }, delay);
    }
  }, [ui.state.flags.coverNeedsChecking, ui.state.dialogs.coverSpineChangeNotification]);

  const onCloseTip = (payload: Partial<OrderButtonTipState>) => {
    setTipState(current => {
      return {
        ...current,
        ...payload,
      };
    });
  };

  const defaultLabel = useCallback(() => {
    const {orderButtonLabel, submitEditedOrderButtonLabel} = experience.SETTINGS;

    if (ui.state.textIsProcessing) {
      return t('*:str_TitleSavingProject');
    }

    if (orderButtonLabel.orderButtonLabel === 'prod') {
      if (submitEditedOrderButtonLabel.submitEditedOrderButtonLabel === 'sbmt') {
        return t('*:str_LabelSubmit');
      } else {
        if (submitEditedOrderButtonLabel.customEditedOrderButtonLabel)
          return Localisation.getLocaleString(
            Lang.getCode(),
            submitEditedOrderButtonLabel.customEditedOrderButtonLabel
          );
      }
    } else if (orderButtonLabel.orderButtonLabel === 'cust') {
      return Localisation.getLocaleString(Lang.getCode(), orderButtonLabel.customOrderButtonLabel);
    } else if (orderButtonLabel.orderButtonLabel === 'atbkt') {
      return t('*:str_ButtonAddToBasket');
    } else if (orderButtonLabel.orderButtonLabel === 'chko') {
      return t('*:str_ButtonCheckout');
    }

    return t('*:str_ButtonAddToBasket');
  }, [ui.state.textIsProcessing]);

  return (
    <>
      <Button
        id="mainOrderButton"
        buttonStyle="primary"
        size={
          ui.state.isSmallScreen && experience.EDITOR.header.headerHeight === 'sm'
            ? 'small'
            : 'medium'
        }
        label={
          !showProgress
            ? defaultLabel()
            : t('helptips:str_MessagePleaseWaitPercentage').replace('^0', String(totalPercentage))
        }
        hideLabel={ui.state.isSmallScreen}
        startIcon={<CartIcon />}
        labelLines={2}
        labelAlignmentWhenWrapped="left"
        className="offsetFocus"
        onClick={() => {
          if (ui.state.textIsProcessing) {
            setTipState(current => {
              return {
                ...current,
                textIsProcessing: true,
              };
            });
            return;
          }
          // if we are still uploading pictures, allow the order helptip to show
          if (total > 0 && hiresPercentage < 100) {
            setShowProgress(true);
            setTipState(current => {
              return {
                ...current,
                uploadProgress: true,
              };
            });
            return;
          }

          if (
            layout.retroprints &&
            (totalPrints < min || totalPrints > max) &&
            !tipState.uploadProgress
          ) {
            setTipState(current => {
              return {
                ...current,
                minimunNumOfPrints: true,
              };
            });
            return;
          }

          // We always need to check this as the page count may not have changed.
          if (!hasValidPageCount(productLayout.pagecount, productLayout.pageinsertcount)) {
            ui.dispatch(UserInterfaceActions.setFlags({pageCountChanged: true}));
            return;
          }

          // We can check the flag here as the spine can't resize unless the page count is changed.
          if (ui.state.flags.coverNeedsChecking) {
            // Set again to force the dialog to open.
            ui.dispatch(UserInterfaceActions.setFlags({coverNeedsChecking: true}));
            ui.dispatch(
              UserInterfaceActions.setDialogStatus({
                coverSpineChangeNotification: DialogStatus.Open,
              })
            );
            return;
          }

          userFlow.activateUserFlow(UserFlowType.Order);
        }}
        ref={orderButtonRef}
        showToolTip={false}
      />

      <HelpTip
        open={tipState.minimunNumOfPrints}
        anchorId={'mainOrderButton'}
        anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Right}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
        buttonFunction={() => onCloseTip({minimunNumOfPrints: false})}
        onClose={() => onCloseTip({minimunNumOfPrints: false})}
        offset={{x: 0, y: 5}}
        className={'lg:max-w-[300px]'}
        buttonProps={{
          buttonStyle: 'tertiary',
        }}
        arrow
        theme={ThemeName.Warning}
        message={t('retroprint:str_WarningPageCountLimitReached')
          .replace('^0', String(min))
          .replace('^1', String(max))}
      />

      <UploadWarningTip
        open={tipState.uploadProgress}
        anchorId={'mainOrderButton'}
        anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Right}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
        buttonFunction={() => onCloseTip({uploadProgress: false})}
        title={t('helptips:str_TitleStillUploadingPictures').replace('^0', String(totalPercentage))}
        message={t('helptips:str_MessageOrderWhenPicturesHaveUploaded')}
      />

      <HelpTip
        open={tipState.readyToOrder}
        message={t('helptips:str_MessageReadyToOrder')}
        anchorId={'mainOrderButton'}
        anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Right}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
        offset={{x: 0, y: 5}}
        className={'w-[300rem]'}
        buttonFunction={() => onCloseTip({readyToOrder: false})}
        arrow
        excludeIdFromClickOutside={'mainOrderButton'}
        title={t('helptips:str_TitleReadyToOrder')}
        repositionOnResize
      />

      <HelpTip
        open={tipState.textIsProcessing}
        message={t('*:str_MessageWarningProjectCurrentlySaving')}
        anchorId={'mainOrderButton'}
        anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Right}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
        offset={{x: 0, y: 5}}
        className={'w-[300rem]'}
        buttonFunction={() => onCloseTip({textIsProcessing: false})}
        arrow
        excludeIdFromClickOutside={'mainOrderButton'}
        title={t('*:str_TitlePleaseWait')}
        repositionOnResize
      />
    </>
  );
};

export default OrderButton;
